import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { User, Check, X, Clock, Repeat, MessageCircle, Award, RefreshCw, UserCircle, LayersIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { CopyableFriendCode } from '../../components/friend-codes/CopyableFriendCode';
import { PageLayout } from '../../components/layout/PageLayout';
import { PageTitle } from '../../components/layout/PageTitle';
import { Helmet } from 'react-helmet-async';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';

// Types pour les objets de l'échange
interface Card {
  id: string;
  card_fr: string;
  card_en?: string;
  set: string;
  rarity: string;
  image: string;
}

interface TradeCard {
  id: string;
  card: Card;
}

interface UserProfile {
  user_id: string;
  username: string;
  avatar_url?: string;
}

interface SetInfo {
  name: string;
  display_name: string;
  display_name_en?: string;
  img: string;
}

interface Trade {
  id: string;
  created_at: string;
  status: 'pending' | 'accepted' | 'completed';
  user_id: string;
  wanted_card_id: string;
  wanted_card: Card;
  trade_cards: TradeCard[];
  accepted_trade_card?: TradeCard;
  creator: UserProfile;
  accepted_by?: UserProfile;
  creator_pseudo?: string;
  creator_pokemon_id?: string;
  acceptor_pseudo?: string;
  acceptor_pokemon_id?: string;
}

const TradeStatus = {
  pending: { label: 'trades.status.pending', Icon: Clock, className: 'text-amber-600 bg-amber-50 border-amber-200' },
  accepted: { label: 'trades.status.accepted', Icon: Check, className: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  completed: { label: 'trades.status.completed', Icon: Award, className: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
};

const LoadingSkeleton = () => (
  <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse">
    {/* En-tête */}
    <div className="h-10 w-64 bg-gray-200 rounded-full mb-6" />

    {/* Statut */}
    <div className="mb-8">
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-200" />
            <div>
              <div className="h-5 w-32 bg-gray-200 rounded-full mb-2" />
              <div className="h-4 w-24 bg-gray-200 rounded-full" />
            </div>
          </div>
          <div>
            <div className="h-5 w-24 bg-gray-200 rounded-full mb-2" />
            <div className="h-4 w-32 bg-gray-200 rounded-full" />
          </div>
        </div>
      </div>
    </div>

    {/* Cartes */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
      {[0, 1].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="h-8 w-40 bg-gray-200 rounded-full m-6" />
          <div className="flex justify-center p-8">
            <div className="w-64 h-64 bg-gray-200 rounded-xl" />
          </div>
          <div className="px-6 pb-6">
            <div className="h-5 w-48 bg-gray-200 rounded-full mb-2" />
            <div className="h-4 w-32 bg-gray-200 rounded-full" />
          </div>
        </div>
      ))}
    </div>

    {/* Actions */}
    <div className="mb-8">
      <div className="h-6 w-32 bg-gray-200 rounded-full mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="h-12 bg-gray-200 rounded-xl" />
        <div className="h-12 bg-gray-200 rounded-xl" />
      </div>
    </div>
  </div>
);

// Fonction pour calculer le coût de l'échange en fonction de la rareté
const getCardCost = (rarity: string): number => {
  const costMap: { [key: string]: number } = {
    '♢': 0,
    '♢♢': 0,
    '♢♢♢': 120,
    '♢♢♢♢': 500,
    '☆': 400,
    '☆☆': -1, // Non échangeable
    '☆☆☆': -1, // Non échangeable
    '♛': -1 // Non échangeable
  };
  return costMap[rarity] || 0;
};

export const TradeDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang, getPathForLang } = useTranslation();
  const [trade, setTrade] = useState<Trade | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    action: 'cancel' | 'complete' | null;
  }>({ isOpen: false, action: null });
  
  // État pour les informations des sets
  const [setInfos, setSetInfos] = useState<Record<string, SetInfo>>({});
  const [loadingSets, setLoadingSets] = useState(false);

  // Fonction pour obtenir le nom de la carte en fonction de la langue
  const getCardName = (card: Card) => {
    return lang === 'en' ? (card.card_en || card.card_fr) : card.card_fr;
  };

  // Fonction pour obtenir l'URL de l'image en fonction de la langue
  const getImageUrl = (imagePath: string) => {
    return `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${imagePath}`;
  };

  const handleUpdateTradeStatus = async (newStatus: 'pending' | 'completed') => {
    if (!trade || isUpdating) return;
    
    setIsUpdating(true);
    try {
      const updateData = newStatus === 'pending' 
        ? {
            status: newStatus,
            accepted_by: null,
            accepted_card_id: null,
            acceptor_pokemon_id: null
          }
        : { status: newStatus };

      const { error } = await supabase
        .from('trades')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      
      setTrade((prev) => {
        if (!prev) return null;
        return { 
          ...prev, 
          status: newStatus,
          ...(newStatus === 'pending' && {
            accepted_by: undefined,
            accepted_card_id: null,
            acceptor_pokemon_id: undefined
          })
        };
      });

      // Redirection vers la page des échanges si annulation
      if (newStatus === 'pending') {
        navigate(getPathForLang('/trades/my-trades', lang));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleContactUser = async () => {
    if (!trade || !user) return;
    
    const otherUserId = user.id === trade.creator.user_id ? trade.accepted_by?.user_id : trade.creator.user_id;
    
    if (!otherUserId) return;

    try {
      // Vérifier si une conversation existe déjà
      const { data: existingConversations, error: conversationsError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations!inner(id)
        `)
        .eq('user_id', user.id);

      if (conversationsError) throw conversationsError;

      // Parmi ces conversations, trouver celle qui contient l'autre utilisateur
      const { data: existingConversation } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', otherUserId)
        .in('conversation_id', existingConversations?.map(c => c.conversation_id) || [])
        .single();

      if (existingConversation) {
        // Si une conversation existe, rediriger vers celle-ci
        navigate(getPathForLang(`/messages/${existingConversation.conversation_id}`, lang));
      } else {
        // Créer une nouvelle conversation
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (createError) throw createError;

        // Ajouter les participants
        const participants = [
          {
            conversation_id: newConversation.id,
            user_id: user.id
          },
          {
            conversation_id: newConversation.id,
            user_id: otherUserId
          }
        ];

        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert(participants);

        if (participantsError) throw participantsError;

        // Rediriger vers la nouvelle conversation
        navigate(getPathForLang(`/messages/${newConversation.id}`, lang));
      }
    } catch (error) {
      console.error('Erreur lors de la création de la conversation:', error);
    }
  };

  // Fonction pour charger les informations des sets
  const loadSetInfos = async (sets: string[]) => {
    if (!sets.length) return;
    
    try {
      setLoadingSets(true);
      const { data, error } = await supabase
        .from('sets')
        .select('*')
        .in('name', sets);

      if (error) throw error;
      
      if (data) {
        const setsMap: Record<string, SetInfo> = {};
        data.forEach(set => {
          setsMap[set.name] = set;
        });
        setSetInfos(setsMap);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des informations des sets:', error);
    } finally {
      setLoadingSets(false);
    }
  };

  useEffect(() => {
    const loadTrade = async () => {
      if (!user || !id) return;

      try {
        const { data, error } = await supabase
          .from('trades')
          .select(`
            *,
            wanted_card:cards!trades_wanted_card_id_fkey(*),
            trade_cards!trade_cards_trade_id_fkey(
              id,
              card:cards!trade_cards_card_id_fkey(*)
            ),
            accepted_trade_card:trade_cards!trades_accepted_card_id_fkey(
              id,
              card:cards!trade_cards_card_id_fkey(*)
            ),
            creator:profiles!trades_user_id_fkey1(*),
            accepted_by:profiles!trades_accepted_by_fkey1(*)
          `)
          .eq('id', id)
          .single();

        if (error) throw error;
        
        const tradeData = data as Trade;
        setTrade(tradeData);
        
        // Collecter tous les sets uniques pour charger leurs informations
        const sets = new Set<string>();
        if (tradeData.wanted_card?.set) {
          sets.add(tradeData.wanted_card.set);
        }
        
        if (tradeData.accepted_trade_card?.card?.set) {
          sets.add(tradeData.accepted_trade_card.card.set);
        }
        
        tradeData.trade_cards?.forEach(tc => {
          if (tc.card?.set) {
            sets.add(tc.card.set);
          }
        });
        
        // Charger les informations des sets
        if (sets.size > 0) {
          loadSetInfos(Array.from(sets));
        }
      } catch (error) {
        console.error('Error loading trade:', error);
        navigate(getPathForLang('/trades/my-trades', lang));
      } finally {
        setLoading(false);
      }
    };

    loadTrade();
  }, [id, user, navigate, lang]);


  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!trade) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('trades.not_found.title')}
        </h2>
        <p className="text-gray-500 mb-6">
          {t('trades.not_found.message')}
        </p>
        <Link
          to={getPathForLang('/trades/browse', lang)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Repeat className="w-5 h-5 mr-2" />
          {t('trades.not_found.back_to_trades')}
        </Link>
      </div>
    );
  }

  const status = TradeStatus[trade.status];
  const StatusIcon = status.Icon;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Composant pour afficher l'image du set
  const SetImage = ({ setName }: { setName: string }) => {
    const setInfo = setInfos[setName];
    const { lang } = useTranslation();
    
    if (loadingSets) {
      return (
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="w-20 h-20 bg-gray-100 rounded-md animate-pulse"></div>
          <span className="text-sm text-gray-500 animate-pulse">{setName}</span>
        </div>
      );
    }
    
    if (!setInfo || !setInfo.img) {
      return (
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="w-20 h-20 bg-gray-100 rounded-md flex items-center justify-center">
            <LayersIcon className="w-8 h-8 text-gray-400" />
          </div>
          <span className="text-sm text-gray-500">{setName}</span>
        </div>
      );
    }
    
    const displayName = lang === 'en' ? (setInfo.display_name_en || setInfo.display_name) : setInfo.display_name;
    const imageUrl = `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${setInfo.img}`;
    
    return (
      <div className="flex flex-col items-center justify-center gap-2">
        <div className="relative w-20 h-20 flex items-center justify-center bg-gray-50 rounded-md p-1">
          <img 
            src={imageUrl}
            alt={displayName || setName}
            className="max-h-16 max-w-16 object-contain" 
          />
        </div>
        <span className="text-sm text-gray-600 font-medium">{displayName || setName}</span>
      </div>
    );
  };

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: t('trades.breadcrumb.trades'), path: getPathForLang('/trades/browse', lang), icon: <Repeat className="w-4 h-4" /> },
          { label: t('trades.breadcrumb.trade_details'), path: getPathForLang(`/trades/${id}`, lang) }
        ]
      }}
    >
      <Helmet>
        <title>{`${t('trades.detail.meta.title', { card: getCardName(trade.wanted_card) })}`}</title>
        <meta name="description" content={`${t('trades.detail.meta.description', { 
          card: getCardName(trade.wanted_card),
          rarity: trade.wanted_card.rarity,
          status: t(status.label),
          date: formatDate(trade.created_at)
        })}`} />
        <meta name="keywords" content={`${t('trades.detail.meta.keywords', { 
          card: getCardName(trade.wanted_card),
          set: trade.wanted_card.set,
          rarity: trade.wanted_card.rarity
        })}`} />
        <meta property="og:title" content={`Échange de carte Pokémon: ${trade.wanted_card.card_fr}`} />
        <meta property="og:description" content={`Échange de carte Pokémon TCG Pocket: ${trade.wanted_card.card_fr} (${trade.wanted_card.rarity}). Statut: ${status.label}.`} />
        <meta property="og:image" content={`${SUPABASE_IMAGE_BASE_URL}${trade.wanted_card.image}`} />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Échange de carte Pokémon: ${trade.wanted_card.card_fr}`} />
        <meta name="twitter:description" content={`Échange de carte Pokémon TCG Pocket: ${trade.wanted_card.card_fr} (${trade.wanted_card.rarity}). Statut: ${status.label}.`} />
        <meta name="twitter:image" content={`${SUPABASE_IMAGE_BASE_URL}${trade.wanted_card.image}`} />
        <link rel="canonical" href={window.location.href} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Pokémon TCG Pocket" />
        <meta property="og:site_name" content="Pokémon TCG Pocket" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:url" content={window.location.href} />
        
        {/* Données structurées JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": `Carte Pokémon ${trade.wanted_card.card_fr}`,
            "description": `Échange de carte Pokémon TCG Pocket: ${trade.wanted_card.card_fr} (${trade.wanted_card.rarity})`,
            "image": `${SUPABASE_IMAGE_BASE_URL}${trade.wanted_card.image}`,
            "offers": {
              "@type": "Offer",
              "availability": trade.status === 'pending' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              "price": getCardCost(trade.wanted_card.rarity),
              "priceCurrency": "Jetons",
              "seller": {
                "@type": "Person",
                "name": trade.creator.username
              }
            },
            "category": "Cartes Pokémon à échanger",
            "brand": {
              "@type": "Brand",
              "name": "Pokémon TCG Pocket"
            },
            "additionalProperty": [
              {
                "@type": "PropertyValue",
                "name": "Set",
                "value": trade.wanted_card.set
              },
              {
                "@type": "PropertyValue",
                "name": "Rareté",
                "value": trade.wanted_card.rarity
              },
              {
                "@type": "PropertyValue",
                "name": "Statut",
                "value": status.label
              }
            ]
          })}
        </script>
        
        {/* Données structurées pour l'échange */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ExchangeRateSpecification",
            "name": `Échange de carte Pokémon: ${trade.wanted_card.card_fr}`,
            "description": `Échange proposé par ${trade.creator.username}. Carte recherchée: ${trade.wanted_card.card_fr}`,
            "currentExchangeRate": {
              "@type": "UnitPriceSpecification",
              "price": getCardCost(trade.wanted_card.rarity),
              "priceCurrency": "Jetons"
            },
            "exchangeRateSpread": "0",
            "currency": "Jetons",
            "currentExchangeRateValidUntil": trade.status === 'pending' ? new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString() : new Date().toISOString(),
            "provider": {
              "@type": "Person",
              "name": trade.creator.username
            }
          })}
        </script>
        
        {/* Données structurées pour la page web */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": `Échange de carte Pokémon: ${trade.wanted_card.card_fr}`,
            "description": `Échange de carte Pokémon TCG Pocket: ${trade.wanted_card.card_fr} (${trade.wanted_card.rarity}). Statut: ${status.label}.`,
            "url": window.location.href,
            "datePublished": trade.created_at,
            "dateModified": new Date().toISOString(),
            "publisher": {
              "@type": "Organization",
              "name": "Pokémon TCG Pocket",
              "logo": {
                "@type": "ImageObject",
                "url": "https://pokemonpocket.fr/logo.png"
              }
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Échanges",
                  "item": "https://pokemonpocket.fr/trades/browse"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Mes Échanges",
                  "item": "https://pokemonpocket.fr/trades/my-trades"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": `Détails: ${trade.wanted_card.card_fr}`,
                  "item": window.location.href
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <PageTitle title={t('trades.trade_details')} />
        
        {/* En-tête avec le statut de l'échange */}
        <div className="mb-8 bg-gradient-to-br from-white to-gray-50 shadow-md rounded-2xl p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">
                {t('trades.detail.title')}
              </h1>
              <p className="text-gray-600 mb-4 md:mb-0">
                {t('trades.detail.created_on')} {formatDate(trade.created_at)}
              </p>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              <div className={`flex items-center px-4 py-2 rounded-full ${status.className} border`}>
                <StatusIcon className="w-5 h-5 mr-2" />
                <span className="font-medium">{t(status.label)}</span>
              </div>
            </div>
          </div>
          {trade.wanted_card && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-600">{t('trades.detail.trade_rarity')}:</span>
                <div className="flex items-center gap-2">
                  <div className={`px-3 py-1.5 rounded-full bg-indigo-600 text-white text-sm font-medium shadow-sm flex items-center gap-2`}>
                    <span>{trade.wanted_card.rarity}</span>
                  </div>
                  
                  {getCardCost(trade.wanted_card.rarity) !== -1 ? (
                    <div className="flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
                      <img 
                        src="https://blog.pokemonpocket.fr/wp-content/uploads/2025/01/piece-echange.png"
                        alt={t('trades.detail.exchange_token')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium text-blue-700">{getCardCost(trade.wanted_card.rarity)} {t('trades.detail.tokens')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 bg-red-50 px-3 py-1.5 rounded-full border border-red-200">
                      <X className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-red-600">{t('trades.detail.not_tradeable')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Annonce AdSense en haut de page */}
        <div className="mt-6 mb-8 w-full flex justify-center items-center overflow-hidden">
          <div className="w-full max-w-[320px] sm:max-w-none mx-auto flex justify-center items-center">
            <AdSense adSlot="1424794127" id="page-top" />
          </div>
        </div>

        {/* Conteneur principal */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Colonne gauche - Carte à donner */}
          <div className="md:col-span-1 bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-orange-100">
              <h2 className="text-lg font-bold text-gray-900 flex items-center">
                <span className="w-8 h-8 flex items-center justify-center mr-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-sm font-bold">1</span>
                {t('trades.detail.card_to_give')}
              </h2>
            </div>
            <div className="p-6">
              {user?.id === trade.creator.user_id ? (
                // Si l'utilisateur est le créateur, afficher la carte acceptée ou message d'attente
                trade.status === 'accepted' || trade.status === 'completed' ? (
                  trade.accepted_trade_card && (
                    <div className="flex flex-col items-center">
                      <div className="relative group mb-4">
                        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl opacity-30 blur-sm group-hover:opacity-40 transition-all duration-500"></div>
                        <Link to={getPathForLang(`/sets/${trade.accepted_trade_card.card.set}/cards/${trade.accepted_trade_card.card.id}`, lang)} className="relative block">
                          <img
                            src={getImageUrl(trade.accepted_trade_card.card.image)}
                            alt={getCardName(trade.accepted_trade_card.card)}
                            className="w-full max-w-[220px] mx-auto object-contain transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                          />
                        </Link>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900 mb-1 hover:text-amber-600 transition-colors">
                          <Link to={getPathForLang(`/sets/${trade.accepted_trade_card.card.set}/cards/${trade.accepted_trade_card.card.id}`, lang)}>
                            {getCardName(trade.accepted_trade_card.card)}
                          </Link>
                        </p>
                        <div className="flex flex-col items-center mt-3">
                          <SetImage setName={trade.accepted_trade_card.card.set} />

                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  // Afficher un message d'attente
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                      <Clock className="w-8 h-8 text-amber-600" />
                    </div>
                    <p className="text-base text-gray-600 max-w-xs">
                      {t('trades.detail.waiting_for_partner')}
                    </p>
                  </div>
                )
              ) : (
                // Si l'utilisateur est l'accepteur, afficher la carte wanted
                <div className="flex flex-col items-center">
                  <div className="relative group mb-4">
                    <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl opacity-30 blur-sm group-hover:opacity-40 transition-all duration-500"></div>
                    <Link to={getPathForLang(`/sets/${trade.wanted_card.set}/cards/${trade.wanted_card.id}`, lang)} className="relative block">
                      <img
                        src={getImageUrl(trade.wanted_card.image)}
                        alt={getCardName(trade.wanted_card)}
                        className="w-full max-w-[220px] mx-auto object-contain transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                      />
                    </Link>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900 mb-1 hover:text-amber-600 transition-colors">
                      <Link to={getPathForLang(`/sets/${trade.wanted_card.set}/cards/${trade.wanted_card.id}`, lang)}>
                        {getCardName(trade.wanted_card)}
                      </Link>
                    </p>
                    <div className="flex flex-col items-center mt-3">
                      <SetImage setName={trade.wanted_card.set} />

                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Colonne centrale - Carte à recevoir */}
          <div className="md:col-span-1 bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
              <h2 className="text-lg font-bold text-gray-900 flex items-center">
                <span className="w-8 h-8 flex items-center justify-center mr-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-bold">2</span>
                {t('trades.detail.card_to_receive')}
              </h2>
            </div>
            <div className="p-6">
              {user?.id === trade.creator.user_id ? (
                // Si l'utilisateur est le créateur, afficher la carte wanted
                <div className="flex flex-col items-center">
                  <div className="relative group mb-4">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl opacity-30 blur-sm group-hover:opacity-40 transition-all duration-500"></div>
                    <Link to={getPathForLang(`/sets/${trade.wanted_card.set}/cards/${trade.wanted_card.id}`, lang)} className="relative block">
                      <img
                        src={getImageUrl(trade.wanted_card.image)}
                        alt={getCardName(trade.wanted_card)}
                        className="w-full max-w-[220px] mx-auto object-contain transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                      />
                    </Link>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900 mb-1 hover:text-indigo-600 transition-colors">
                      <Link to={getPathForLang(`/sets/${trade.wanted_card.set}/cards/${trade.wanted_card.id}`, lang)}>
                        {getCardName(trade.wanted_card)}
                      </Link>
                    </p>
                    <div className="flex flex-col items-center mt-3">
                      <SetImage setName={trade.wanted_card.set} />

                    </div>
                  </div>
                </div>
              ) : (
                // Si l'utilisateur est l'accepteur
                trade.status === 'accepted' || trade.status === 'completed' ? (
                  // Afficher la carte acceptée
                  trade.accepted_trade_card && (
                    <div className="flex flex-col items-center">
                      <div className="relative group mb-4">
                        <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl opacity-30 blur-sm group-hover:opacity-40 transition-all duration-500"></div>
                        <Link to={getPathForLang(`/sets/${trade.accepted_trade_card.card.set}/cards/${trade.accepted_trade_card.card.id}`, lang)} className="relative block">
                          <img
                            src={getImageUrl(trade.accepted_trade_card.card.image)}
                            alt={getCardName(trade.accepted_trade_card.card)}
                            className="w-full max-w-[220px] mx-auto object-contain transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                          />
                        </Link>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900 mb-1 hover:text-emerald-600 transition-colors">
                          <Link to={getPathForLang(`/sets/${trade.accepted_trade_card.card.set}/cards/${trade.accepted_trade_card.card.id}`, lang)}>
                            {getCardName(trade.accepted_trade_card.card)}
                          </Link>
                        </p>
                        <div className="flex flex-col items-center mt-3">
                          <SetImage setName={trade.accepted_trade_card.card.set} />

                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  // Afficher la grille des cartes proposées
                  <div className="grid grid-cols-2 gap-3 h-full">
                    {trade.trade_cards.map((tc: TradeCard) => (
                      <div
                        key={tc.id}
                        className="group bg-gray-50 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md"
                      >
                        <Link to={getPathForLang(`/sets/${tc.card.set}/cards/${tc.card.id}`, lang)} className="block p-2">
                          <div className="relative overflow-hidden rounded-lg mb-2">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                            <img
                              src={getImageUrl(tc.card.image)}
                              alt={getCardName(tc.card)}
                              className="w-full aspect-square object-contain transform transition duration-500 group-hover:scale-110"
                            />
                          </div>
                          <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                            {getCardName(tc.card)}
                          </p>
                          <div className="flex flex-col items-center mt-2">
                            <SetImage setName={tc.card.set} />

                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Colonne droite - Informations de contact et actions */}
          <div className="md:col-span-1">
            {/* Contact */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  {t('trades.detail.contact')}
                </h2>
              </div>
              <div className="p-6">
                <div className="flex items-center mb-6">
                  {user?.id === trade.creator.user_id ? (
                    // Si l'utilisateur est le créateur, afficher les infos de l'accepteur
                    trade.accepted_by ? (
                      <>
                        {trade.accepted_by.avatar_url ? (
                          <Link to={getPathForLang(`/users/${trade.accepted_by.username}`, lang)} className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-70 blur-sm transition-opacity duration-300"></div>
                            <img
                              src={trade.accepted_by.avatar_url}
                              alt={trade.accepted_by.username}
                              className="w-12 h-12 rounded-full object-cover border-2 border-indigo-100 mr-3 relative"
                            />
                          </Link>
                        ) : (
                          <Link to={getPathForLang(`/users/${trade.accepted_by.username}`, lang)} className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-70 blur-sm transition-opacity duration-300"></div>
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center mr-3 relative">
                              <User className="w-6 h-6 text-white" />
                            </div>
                          </Link>
                        )}
                        <div>
                          <Link 
                            to={getPathForLang(`/users/${trade.accepted_by.username}`, lang)}
                            className="block text-lg font-bold text-gray-900 hover:text-indigo-600 transition-colors group flex items-center"
                          >
                            {trade.accepted_by.username}
                            <UserCircle className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                          <span className="text-sm text-gray-500">{t('trades.detail.trade_partner')}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-center w-full py-4">
                        <RefreshCw className="w-5 h-5 text-gray-400 mr-2 animate-spin" />
                        <p className="text-gray-500">{t('trades.detail.waiting_for_partner')}</p>
                      </div>
                    )
                  ) : (
                    // Si l'utilisateur est l'accepteur ou un visiteur, afficher les infos du créateur
                    <>
                      {trade.creator.avatar_url ? (
                        <Link to={getPathForLang(`/users/${trade.creator.username}`, lang)} className="relative group">
                          <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-70 blur-sm transition-opacity duration-300"></div>
                          <img
                            src={trade.creator.avatar_url}
                            alt={trade.creator.username}
                            className="w-12 h-12 rounded-full object-cover border-2 border-indigo-100 mr-3 relative"
                          />
                        </Link>
                      ) : (
                        <Link to={getPathForLang(`/users/${trade.creator.username}`, lang)} className="relative group">
                          <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full opacity-0 group-hover:opacity-70 blur-sm transition-opacity duration-300"></div>
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center mr-3 relative">
                            <User className="w-6 h-6 text-white" />
                          </div>
                        </Link>
                      )}
                      <div>
                        <Link 
                          to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                          className="block text-lg font-bold text-gray-900 hover:text-indigo-600 transition-colors group flex items-center"
                        >
                          {trade.creator.username}
                          <UserCircle className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                        <span className="text-sm text-gray-500">{t('trades.detail.trade_creator')}</span>
                      </div>
                    </>
                  )}
                </div>

                {user?.id === trade.creator.user_id ? (
                  // Afficher les infos de l'accepteur si disponibles
                  trade.accepted_by && (
                    <div className="space-y-4">
                      {trade.acceptor_pseudo && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('trades.detail.game_username')}</p>
                          <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-center text-gray-900 font-medium">
                            {trade.acceptor_pseudo}
                          </div>
                        </div>
                      )}
                      {trade.acceptor_pokemon_id && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{t('trades.detail.friend_code')}</p>
                          <CopyableFriendCode code={trade.acceptor_pokemon_id} />
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  // Afficher les infos du créateur
                  <div className="space-y-4">
                    {trade.creator_pseudo && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{t('trades.detail.game_username')}</p>
                        <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-center text-gray-900 font-medium">
                          {trade.creator_pseudo}
                        </div>
                      </div>
                    )}
                    {trade.creator_pokemon_id && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{t('trades.detail.friend_code')}</p>
                        <CopyableFriendCode code={trade.creator_pokemon_id} />
                      </div>
                    )}
                  </div>
                )}

                {/* Option d'affichage profil complet */}
                {((user?.id === trade.creator.user_id && trade.accepted_by) || 
                   (user?.id !== trade.creator.user_id && trade.creator)) && (
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <Link 
                      to={getPathForLang(`/users/${trade.creator.username}`, lang)}
                      className="w-full py-3 px-4 flex items-center justify-center bg-gradient-to-r from-gray-50 to-slate-50 hover:from-indigo-50 hover:to-blue-50 border border-slate-200 rounded-xl text-gray-800 hover:text-indigo-600 font-medium transition-all"
                    >
                      <UserCircle className="w-5 h-5 mr-2" />
                      {t('trades.detail.view_full_profile')}
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">{t('trades.detail.actions')}</h2>
              </div>
              <div className="p-6 space-y-4">
                {trade.status === 'accepted' && (
                  <>
                    <button
                      onClick={() => setConfirmationModal({ isOpen: true, action: 'complete' })}
                      disabled={isUpdating}
                      className="w-full inline-flex justify-center items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-base font-medium rounded-xl shadow-md hover:from-emerald-600 hover:to-teal-600 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      <Check className="w-5 h-5 mr-2" />
                      {t('trades.detail.validate_trade')}
                    </button>
                    <button
                      onClick={() => setConfirmationModal({ isOpen: true, action: 'cancel' })}
                      disabled={isUpdating}
                      className="w-full inline-flex justify-center items-center px-6 py-3 text-red-600 bg-white border-2 border-red-100 rounded-xl hover:bg-red-50 transition-all text-base font-medium"
                    >
                      <X className="w-5 h-5 mr-2" />
                      {t('trades.detail.cancel_trade')}
                    </button>
                  </>
                )}
                {(trade.status === 'accepted' || trade.status === 'completed') && trade.accepted_by && (
                  <button
                    onClick={handleContactUser}
                    className="w-full inline-flex justify-center items-center px-6 py-3 text-indigo-600 bg-white border-2 border-indigo-100 rounded-xl hover:bg-indigo-50 transition-all text-base font-medium"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {t('trades.detail.contact_user')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal de confirmation */}
        {confirmationModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {confirmationModal.action === 'cancel' 
                  ? t('trades.detail.modal.cancel_title')
                  : t('trades.detail.modal.complete_title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {confirmationModal.action === 'cancel'
                  ? t('trades.detail.modal.cancel_message')
                  : t('trades.detail.modal.complete_message')}
              </p>
              
              {confirmationModal.action === 'complete' && trade?.wanted_card && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center mb-3">
                    <img 
                      src="https://blog.pokemonpocket.fr/wp-content/uploads/2025/01/piece-echange.png"
                      alt={t('trades.detail.exchange_token')}
                      className="w-5 h-5 mr-2"
                    />
                    <span className="text-sm font-medium text-gray-900">{t('trades.detail.exchange_cost')}</span>
                  </div>
                  
                  {getCardCost(trade.wanted_card.rarity) !== -1 ? (
                    <div className="flex items-center justify-center bg-white rounded-lg p-3">
                      <span className="text-lg font-medium text-blue-700">
                        {getCardCost(trade.wanted_card.rarity)} {t('trades.detail.tokens')}
                      </span>
                      <img 
                        src="https://blog.pokemonpocket.fr/wp-content/uploads/2025/01/piece-echange.png"
                        alt={t('trades.detail.tokens')}
                        className="w-5 h-5 ml-2"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center bg-white rounded-lg p-3">
                      <X className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-lg font-medium text-red-600">{t('trades.detail.not_tradeable')}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmationModal({ isOpen: false, action: null })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    setConfirmationModal({ isOpen: false, action: null });
                    handleUpdateTradeStatus(confirmationModal.action === 'cancel' ? 'pending' : 'completed');
                  }}
                  className={`px-4 py-2 text-white rounded-lg transition-colors ${
                    confirmationModal.action === 'cancel'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                  disabled={isUpdating}
                >
                  {isUpdating ? t('common.processing') : t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Annonce AdSense en bas de page */}
        <div className="mt-6 mb-8 w-full flex justify-center items-center overflow-hidden">
          <div className="w-full max-w-[320px] sm:max-w-none mx-auto flex justify-center items-center">
            <AdSense adSlot="1424794127" id="page-bottom" />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}; 